import dayjs from 'dayjs';

import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(minMax);

const isFirstOfYear = (date: dayjs.Dayjs) => date.month() === 0 && date.date() === 1;
const isFirstOfMonth = (date: dayjs.Dayjs) => date.date() === 1;

export function generateDatePaths({ path, from, to }: { path: string; from: Date; to: Date }) {
  const results = [];
  let current = dayjs(from);
  const end = dayjs(to);

  while (current.isSameOrBefore(end, 'day')) {
    if (isFirstOfYear(current) && current.endOf('year').isSameOrBefore(end, 'day')) {
      results.push(`${path}/${current.format('YYYY')}`);
      current = current.add(1, 'year');
    } else if (isFirstOfMonth(current) && current.endOf('month').isSameOrBefore(end, 'day')) {
      results.push(`${path}/${current.format('YYYY/MM')}`);
      current = current.add(1, 'month');
    } else {
      results.push(`${path}/${current.format('YYYY/MM/DD')}`);
      current = current.add(1, 'day');
    }
  }

  return results;
}

export function getTotalDateString({ from, to }: { from: string; to: string }) {
  const results = { years: 0, months: 0, days: 0 };
  let current = dayjs(from);
  const end = dayjs(to);

  while (current.isSameOrBefore(end, 'day')) {
    if (isFirstOfYear(current) && current.endOf('year').isSameOrBefore(end, 'day')) {
      results.years += 1;
      current = current.add(1, 'year');
    } else if (isFirstOfMonth(current) && current.endOf('month').isSameOrBefore(end, 'day')) {
      results.months += 1;
      current = current.add(1, 'month');
    } else {
      results.days += 1;
      current = current.add(1, 'day');
    }
  }

  return `${results.years ? `${results.years} year${results.years > 1 ? 's' : ''}` : ''}
    ${results.months ? ` ${results.months} month${results.months > 1 ? 's' : ''}` : ''}
      ${results.days ? ` ${results.days} day${results.days > 1 ? 's' : ''}` : ''}`;
}

export function hasOverlappingDates(range1: { startDate: string; endDate: string }, range2: { startDate: string; endDate: string }) {
  const normalize = (dateStr: string, isStart: boolean) => {
    if (/^\d{4}-\d{2}$/.test(dateStr)) {
      return isStart ? dayjs(dateStr, 'YYYY-MM').startOf('month') : dayjs(dateStr, 'YYYY-MM').endOf('month');
    }
    return dayjs(dateStr, 'YYYY-MM-DD');
  };

  const start1 = normalize(range1.startDate, true);
  const end1 = normalize(range1.endDate, false);
  const start2 = normalize(range2.startDate, true);
  const end2 = normalize(range2.endDate, false);

  return start1.isSameOrBefore(end2) && start2.isSameOrBefore(end1);
}
